import React, { useState, useEffect } from 'react';
import { investmentAPI } from '../../services/api';
import { Plus, Edit, Trash2, Save, X, Settings, FileDown, Eye, Upload } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import ExcelExportModal from '../ExcelExportModal';
import ColumnVisibilityModal from '../ColumnVisibilityModal';
import DetailViewModal from '../DetailViewModal';
import BulkUploadModal from '../BulkUploadModal';
import { useDateFormat } from '../../hooks/useDateFormat';

const TransactionsTab = ({ investmentId, currency, investment }) => {
  const fmtDate = useDateFormat();
  const { user } = useAuth();
  const { canDelete = false, canEdit = false, canCreate = false, canDownload = false } = usePermissions() || {};
  const [transactions, setTransactions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showCustomFieldsManager, setShowCustomFieldsManager] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [formData, setFormData] = useState({
    date_of_transaction: '',
    type_of_transaction: 'Investment',
    funding_type: 'Primary',
    type_of_securities: '',
    number_of_securities: '',
    consideration_per_security: '',
    total_consideration: '',
    percentage_shareholding: '',
    custom_fields: {}
  });

  const [newCustomField, setNewCustomField] = useState({
    name: '',
    type: 'text',
    options: []
  });

  const securityTypes = investment?.types_of_securities_offered || [];

  useEffect(() => {
    fetchTransactions();
    const fields = loadCustomFieldDefinitions();
    loadColumnVisibility(fields);
  }, [investmentId]);

  const loadCustomFieldDefinitions = () => {
    const saved = localStorage.getItem(`custom_fields_${investmentId}`);
    if (saved) {
      const fields = JSON.parse(saved);
      setCustomFieldDefinitions(fields);
      return fields;
    }
    return [];
  };

  const loadColumnVisibility = (customFields = []) => {
    const saved = localStorage.getItem(`column_visibility_transactions_${investmentId}`);
    if (saved) {
      const savedVisibility = JSON.parse(saved);
      // Ensure all custom fields have visibility state (default to true if not present)
      const updatedVisibility = { ...savedVisibility };
      customFields.forEach(field => {
        const key = `custom_${field.id}`;
        if (!(key in updatedVisibility)) {
          updatedVisibility[key] = true;
        }
      });
      setColumnVisibility(updatedVisibility);
    } else {
      // Initialize with all columns visible
      const defaultVisibility = {
        date_of_transaction: true,
        type_of_transaction: true,
        funding_type: true,
        type_of_securities: true,
        number_of_securities: true,
        consideration_per_security: true,
        total_consideration: true,
        percentage_shareholding: true
      };
      // Add custom fields visibility
      customFields.forEach(field => {
        defaultVisibility[`custom_${field.id}`] = true;
      });
      setColumnVisibility(defaultVisibility);
    }
  };

  const saveColumnVisibility = (visibility) => {
    localStorage.setItem(`column_visibility_transactions_${investmentId}`, JSON.stringify(visibility));
    setColumnVisibility(visibility);
  };

  const handleToggleColumn = (columnKey) => {
    const updated = { ...columnVisibility, [columnKey]: !columnVisibility[columnKey] };
    saveColumnVisibility(updated);
  };

  const handleSelectAllColumns = () => {
    const allColumns = getAllColumns().reduce((acc, col) => {
      acc[col.key] = true;
      return acc;
    }, {});
    saveColumnVisibility(allColumns);
  };

  const handleDeselectAllColumns = () => {
    const allColumns = getAllColumns().reduce((acc, col) => {
      acc[col.key] = false;
      return acc;
    }, {});
    saveColumnVisibility(allColumns);
  };

  const getAllColumns = () => {
    const baseColumns = [
      { key: 'date_of_transaction', label: 'Date', required: true },
      { key: 'type_of_transaction', label: 'Transaction Type', required: true },
      { key: 'funding_type', label: 'Funding Type', required: false },
      { key: 'type_of_securities', label: 'Security Type', required: false },
      { key: 'number_of_securities', label: 'Number of Securities', required: false },
      { key: 'consideration_per_security', label: `Price per Security (${currency})`, required: false },
      { key: 'total_consideration', label: `Total Consideration (${currency})`, required: true },
      { key: 'percentage_shareholding', label: '% Holding', required: false }
    ];

    // Add custom field columns
    const customColumns = customFieldDefinitions.map(field => ({
      key: `custom_${field.id}`,
      label: `Custom: ${field.name}`,
      required: false
    }));

    return [...baseColumns, ...customColumns];
  };

  const isColumnVisible = (columnKey) => {
    return columnVisibility[columnKey] !== false;
  };

  const handleRowClick = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDetailModal(true);
  };

  const saveCustomFieldDefinitions = (definitions) => {
    localStorage.setItem(`custom_fields_${investmentId}`, JSON.stringify(definitions));
    setCustomFieldDefinitions(definitions);
    
    // Update column visibility to include new custom fields (default visible)
    const updatedVisibility = { ...columnVisibility };
    definitions.forEach(field => {
      const key = `custom_${field.id}`;
      if (!(key in updatedVisibility)) {
        updatedVisibility[key] = true;
      }
    });
    setColumnVisibility(updatedVisibility);
    localStorage.setItem(`column_visibility_transactions_${investmentId}`, JSON.stringify(updatedVisibility));
  };

  const fetchTransactions = async () => {
    try {
      const response = await investmentAPI.getTransactions(investmentId);
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare data based on transaction type
      let dataToSend = { ...formData };
      
      // For Cost Capitalized, remove security fields and ensure total_consideration is sent
      if (formData.type_of_transaction === 'Cost Capitalized') {
        delete dataToSend.type_of_securities;
        delete dataToSend.number_of_securities;
        delete dataToSend.consideration_per_security;
        // Ensure total_consideration is a number
        if (dataToSend.total_consideration) {
          dataToSend.total_consideration = parseFloat(dataToSend.total_consideration);
        }
      } else {
        // For Investment/Exit, remove total_consideration and ensure security fields are numbers
        delete dataToSend.total_consideration;
        if (dataToSend.number_of_securities) {
          dataToSend.number_of_securities = parseFloat(dataToSend.number_of_securities);
        }
        if (dataToSend.consideration_per_security) {
          dataToSend.consideration_per_security = parseFloat(dataToSend.consideration_per_security);
        }
      }
      
      // Ensure percentage_shareholding is a number if provided
      if (dataToSend.percentage_shareholding) {
        dataToSend.percentage_shareholding = parseFloat(dataToSend.percentage_shareholding);
      } else {
        delete dataToSend.percentage_shareholding;
      }
      
      if (editingId) {
        await investmentAPI.updateTransaction(investmentId, editingId, dataToSend);
      } else {
        await investmentAPI.createTransaction(investmentId, dataToSend);
      }
      fetchTransactions();
      resetForm();
    } catch (error) {
      console.error('Transaction save error:', error);
      alert('Failed to save transaction');
    }
  };

  const handleEdit = (transaction) => {
    setEditingId(transaction.id);
    setFormData({
      date_of_transaction: transaction.date_of_transaction,
      type_of_transaction: transaction.type_of_transaction,
      funding_type: transaction.funding_type || 'Primary',
      type_of_securities: transaction.type_of_securities || '',
      number_of_securities: transaction.number_of_securities || '',
      consideration_per_security: transaction.consideration_per_security || '',
      total_consideration: transaction.total_consideration || '',
      percentage_shareholding: transaction.percentage_shareholding || '',
      custom_fields: transaction.custom_fields || {}
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await investmentAPI.deleteTransaction(investmentId, id);
      fetchTransactions();
    } catch (error) {
      alert('Failed to delete transaction');
    }
  };

  const resetForm = () => {
    setFormData({
      date_of_transaction: '',
      type_of_transaction: 'Investment',
      funding_type: 'Primary',
      type_of_securities: '',
      number_of_securities: '',
      consideration_per_security: '',
      total_consideration: '',
      percentage_shareholding: '',
      custom_fields: {}
    });
    setShowForm(false);
    setEditingId(null);
  };

  const handleCustomFieldChange = (fieldName, value) => {
    setFormData({
      ...formData,
      custom_fields: {
        ...formData.custom_fields,
        [fieldName]: value
      }
    });
  };

  const addCustomField = () => {
    if (!newCustomField.name.trim()) {
      alert('Field name is required');
      return;
    }

    const field = {
      id: Date.now().toString(),
      name: newCustomField.name.trim(),
      type: newCustomField.type,
      options: newCustomField.options
    };

    const updated = [...customFieldDefinitions, field];
    saveCustomFieldDefinitions(updated);
    setNewCustomField({ name: '', type: 'text', options: [] });
  };

  const removeCustomField = (fieldId) => {
    if (!window.confirm('Remove this custom field? Data will be lost.')) return;
    const updated = customFieldDefinitions.filter(f => f.id !== fieldId);
    saveCustomFieldDefinitions(updated);
  };

  const renderCustomField = (field) => {
    const value = formData.custom_fields[field.name] || '';

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            className="w-full px-4 py-2 border rounded-lg"
          />
        );
      case 'number':
        return (
          <input
            type="number"
            step="0.01"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            className="w-full px-4 py-2 border rounded-lg"
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            className="w-full px-4 py-2 border rounded-lg"
          />
        );
      case 'dropdown':
        return (
          <select
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            className="w-full px-4 py-2 border rounded-lg"
          >
            <option value="">Select...</option>
            {field.options.map((opt, idx) => (
              <option key={idx} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'radio':
        return (
          <div className="space-y-2">
            {field.options.map((opt, idx) => (
              <label key={idx} className="flex items-center">
                <input
                  type="radio"
                  name={field.name}
                  value={opt}
                  checked={value === opt}
                  onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                  className="mr-2"
                />
                {opt}
              </label>
            ))}
          </div>
        );
      case 'checkbox':
        return (
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={value === true || value === 'true'}
              onChange={(e) => handleCustomFieldChange(field.name, e.target.checked)}
              className="mr-2"
            />
            Yes
          </label>
        );
      default:
        return null;
    }
  };

  // Bulk upload functions
  const getBulkUploadTemplate = () => {
    const template = {
      'Date *': '2024-01-15',
      'Type *': 'Investment',
      'Funding Type': 'Primary',
      'Security Type': 'Equity Shares',
      'Number of Securities': '1000',
      'Price per Security': '100',
      '% Holding': '10.5'
    };

    // Add custom fields to template
    customFieldDefinitions.forEach(field => {
      template[field.name] = field.type === 'dropdown' || field.type === 'radio' 
        ? (field.options[0] || 'Sample Value')
        : 'Sample Value';
    });

    return template;
  };

  const formatDateForAPI = (dateValue) => {
    if (!dateValue) return null;
    
    // If it's already a string in YYYY-MM-DD format
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    
    // Handle Excel serial date numbers (days since 1899-12-30)
    if (typeof dateValue === 'number') {
      // Excel serial date: number of days since December 30, 1899
      const excelEpoch = new Date(1899, 11, 30); // Dec 30, 1899
      const date = new Date(excelEpoch.getTime() + dateValue * 24 * 60 * 60 * 1000);
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    // If it's a Date object or date string
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      return null; // Invalid date
    }
    
    // Format as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleBulkUpload = async (data) => {
    const results = {
      success: 0,
      failed: 0,
      errors: []
    };

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      
      // Skip empty rows
      if (!row['Date *'] && !row['Date'] && !row['Type *'] && !row['Type']) {
        continue;
      }
      
      try {
        // Format date properly
        const rawDate = row['Date *'] || row['Date'];
        console.log(`Row ${i + 2} - Raw date value:`, rawDate, 'Type:', typeof rawDate);
        const formattedDate = formatDateForAPI(rawDate);
        console.log(`Row ${i + 2} - Formatted date:`, formattedDate);
        
        if (!formattedDate) {
          throw new Error('Invalid date format');
        }

        // Map CSV columns to API fields
        const transactionData = {
          date_of_transaction: formattedDate,
          type_of_transaction: row['Type *'] || row['Type'] || 'Investment',
          funding_type: row['Funding Type'] || undefined,
          type_of_securities: row['Security Type'] || undefined,
          number_of_securities: row['Number of Securities'] ? parseFloat(row['Number of Securities']) : undefined,
          consideration_per_security: row['Price per Security'] ? parseFloat(row['Price per Security']) : undefined,
          percentage_shareholding: row['% Holding'] ? parseFloat(row['% Holding']) : undefined,
          custom_fields: {}
        };

        // Handle Cost Capitalised transactions
        if (transactionData.type_of_transaction === 'Cost Capitalized' && row['Total Consideration']) {
          transactionData.total_consideration = parseFloat(row['Total Consideration']);
          delete transactionData.type_of_securities;
          delete transactionData.number_of_securities;
          delete transactionData.consideration_per_security;
        } else {
          delete transactionData.total_consideration;
        }

        // Extract custom fields
        customFieldDefinitions.forEach(field => {
          if (row[field.name]) {
            transactionData.custom_fields[field.name] = row[field.name];
          }
        });

        // Create transaction
        await investmentAPI.createTransaction(investmentId, transactionData);
        results.success++;
      } catch (error) {
        results.failed++;
        const errorMsg = `Row ${i + 2}: ${error.response?.data?.detail || error.message || 'Failed to create'}`;
        results.errors.push(errorMsg);
      }
    }

    // Refresh the list
    await fetchTransactions();
    
    return results;
  };

  // Excel export columns definition
  const exportColumns = [
    { key: 'date_of_transaction', label: 'Date', format: (val) => val },
    { key: 'type_of_transaction', label: 'Transaction Type', format: (val) => val },
    { key: 'funding_type', label: 'Funding Type', format: (val) => val || 'N/A' },
    { key: 'type_of_securities', label: 'Security Type', format: (val) => val || 'N/A' },
    { key: 'number_of_securities', label: 'Number of Securities', format: (val) => val || 'N/A' },
    { key: 'consideration_per_security', label: `Price per Security (${currency})`, format: (val) => val || 'N/A' },
    { key: 'total_consideration', label: `Total Consideration (${currency})`, format: (val) => val?.toLocaleString() || '0' },
    { key: 'percentage_shareholding', label: '% Holding', format: (val) => val || 'N/A' },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold">Investment Transactions</h3>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowColumnVisibility(true)}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            title="Column Visibility"
          >
            <Eye size={18} className="mr-2" />
            Columns
          </button>
          {canDownload && (
            <button
              onClick={() => setShowExportModal(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              title="Export to Excel (Admin and Fund Manager only)"
            >
              <FileDown size={18} className="mr-2" />
              Export
            </button>
          )}
          {canCreate && (
            <>
              <button
                onClick={() => setShowBulkUpload(true)}
                className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                title="Bulk Upload"
              >
                <Upload size={18} className="mr-2" />
                Bulk Upload
              </button>
              <button
                onClick={() => setShowCustomFieldsManager(!showCustomFieldsManager)}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                title="Manage Custom Fields"
              >
                <Settings size={18} className="mr-2" />
                Custom Fields
              </button>
              {!showForm && (
                <button
                  onClick={() => setShowForm(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus size={18} className="mr-2" />
                  Add Transaction
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Custom Fields Manager */}
      {showCustomFieldsManager && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold">Manage Custom Fields</h4>
            <button onClick={() => setShowCustomFieldsManager(false)} className="text-gray-500">
              <X size={20} />
            </button>
          </div>

          <div className="mb-4 p-4 bg-white rounded border">
            <h5 className="font-medium mb-3">Add New Custom Field</h5>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                type="text"
                placeholder="Field Name"
                value={newCustomField.name}
                onChange={(e) => setNewCustomField({ ...newCustomField, name: e.target.value })}
                className="px-3 py-2 border rounded"
              />
              <select
                value={newCustomField.type}
                onChange={(e) => setNewCustomField({ ...newCustomField, type: e.target.value, options: [] })}
                className="px-3 py-2 border rounded"
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
                <option value="dropdown">Dropdown</option>
                <option value="radio">Radio Button</option>
                <option value="checkbox">Checkbox</option>
              </select>
              <button
                onClick={addCustomField}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add Field
              </button>
            </div>

            {(newCustomField.type === 'dropdown' || newCustomField.type === 'radio') && (
              <div className="mt-3">
                <label className="block text-sm font-medium mb-2">Options (comma-separated):</label>
                <input
                  type="text"
                  placeholder="Option1, Option2, Option3"
                  onChange={(e) => setNewCustomField({
                    ...newCustomField,
                    options: e.target.value.split(',').map(o => o.trim()).filter(o => o)
                  })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
            )}
          </div>

          <div>
            <h5 className="font-medium mb-2">Existing Custom Fields:</h5>
            {customFieldDefinitions.length === 0 ? (
              <p className="text-gray-500 text-sm">No custom fields defined yet</p>
            ) : (
              <div className="space-y-2">
                {customFieldDefinitions.map((field) => (
                  <div key={field.id} className="flex items-center justify-between p-3 bg-white border rounded">
                    <div>
                      <span className="font-medium">{field.name}</span>
                      <span className="ml-2 text-sm text-gray-600">({field.type})</span>
                      {field.options.length > 0 && (
                        <span className="ml-2 text-sm text-gray-500">
                          [{field.options.join(', ')}]
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => removeCustomField(field.id)}
                      className="text-red-600 hover:bg-red-50 p-1 rounded"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transaction Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 p-6 bg-gray-50 rounded-lg border">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold">{editingId ? 'Edit Transaction' : 'Add Transaction'}</h4>
            <button type="button" onClick={resetForm} className="text-gray-500 hover:text-gray-700">
              <X size={20} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">Date *</label>
              <input
                type="date"
                required
                value={formData.date_of_transaction}
                onChange={(e) => setFormData({ ...formData, date_of_transaction: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Type *</label>
              <select
                required
                value={formData.type_of_transaction}
                onChange={(e) => setFormData({ ...formData, type_of_transaction: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="Investment">Investment</option>
                <option value="Cost Capitalized">Cost Capitalized</option>
                <option value="Exit">Exit</option>
              </select>
            </div>
            {formData.type_of_transaction === 'Investment' && (
              <div>
                <label className="block text-sm font-medium mb-2">Funding Type</label>
                <select
                  value={formData.funding_type}
                  onChange={(e) => setFormData({ ...formData, funding_type: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  <option value="Primary">Primary</option>
                  <option value="Secondary">Secondary</option>
                </select>
              </div>
            )}
            
            {/* Show security fields only when NOT Cost Capitalised */}
            {formData.type_of_transaction !== 'Cost Capitalized' && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Security Type *</label>
                  {securityTypes.length > 0 ? (
                    <select
                      required
                      value={formData.type_of_securities}
                      onChange={(e) => setFormData({ ...formData, type_of_securities: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg"
                    >
                      <option value="">Select Security Type</option>
                      {securityTypes.map((type, idx) => (
                        <option key={idx} value={type}>{type}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      required
                      value={formData.type_of_securities}
                      onChange={(e) => setFormData({ ...formData, type_of_securities: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="No securities defined in Portfolio Info"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Number of Securities *</label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    value={formData.number_of_securities}
                    onChange={(e) => setFormData({ ...formData, number_of_securities: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Price per Security ({currency}) *</label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    value={formData.consideration_per_security}
                    onChange={(e) => setFormData({ ...formData, consideration_per_security: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
              </>
            )}
            
            {/* Show total consideration input for Cost Capitalised */}
            {formData.type_of_transaction === 'Cost Capitalized' && (
              <div>
                <label className="block text-sm font-medium mb-2">Total Consideration ({currency}) *</label>
                <input
                  type="number"
                  required
                  step="0.01"
                  value={formData.total_consideration}
                  onChange={(e) => setFormData({ ...formData, total_consideration: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-2">% Shareholding</label>
              <input
                type="number"
                step="0.01"
                value={formData.percentage_shareholding}
                onChange={(e) => setFormData({ ...formData, percentage_shareholding: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>

            {customFieldDefinitions.map((field) => (
              <div key={field.id}>
                <label className="block text-sm font-medium mb-2">{field.name}</label>
                {renderCustomField(field)}
              </div>
            ))}
          </div>

          {formData.type_of_transaction !== 'Cost Capitalized' && formData.number_of_securities && formData.consideration_per_security && (
            <div className="mb-4 p-3 bg-blue-50 rounded">
              <p className="text-sm font-medium">
                Total Consideration: {currency} {(Number(formData.number_of_securities) * Number(formData.consideration_per_security)).toLocaleString()}
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <button type="button" onClick={resetForm} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Save size={18} className="mr-2" />
              {editingId ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {isColumnVisible('date_of_transaction') && (
                <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
              )}
              {isColumnVisible('type_of_transaction') && (
                <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
              )}
              {isColumnVisible('funding_type') && (
                <th className="px-4 py-3 text-left text-sm font-medium">Funding</th>
              )}
              {isColumnVisible('type_of_securities') && (
                <th className="px-4 py-3 text-left text-sm font-medium">Security Type</th>
              )}
              {isColumnVisible('number_of_securities') && (
                <th className="px-4 py-3 text-right text-sm font-medium">Qty</th>
              )}
              {isColumnVisible('consideration_per_security') && (
                <th className="px-4 py-3 text-right text-sm font-medium">Price</th>
              )}
              {isColumnVisible('total_consideration') && (
                <th className="px-4 py-3 text-right text-sm font-medium">Total ({currency})</th>
              )}
              {isColumnVisible('percentage_shareholding') && (
                <th className="px-4 py-3 text-right text-sm font-medium">% Holding</th>
              )}
              {/* Custom Field Columns */}
              {customFieldDefinitions.map((field) => 
                isColumnVisible(`custom_${field.id}`) && (
                  <th key={field.id} className="px-4 py-3 text-left text-sm font-medium">{field.name}</th>
                )
              )}
              <th className="px-4 py-3 text-center text-sm font-medium">View</th>
              {(canEdit || canDelete) && <th className="px-4 py-3 text-right text-sm font-medium">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {transactions.map((t) => (
              <tr key={t.id} className="border-b hover:bg-gray-50">
                {isColumnVisible('date_of_transaction') && (
                  <td className="px-4 py-3 text-sm">{fmtDate(t.date_of_transaction)}</td>
                )}
                {isColumnVisible('type_of_transaction') && (
                  <td className="px-4 py-3 text-sm">{t.type_of_transaction}</td>
                )}
                {isColumnVisible('funding_type') && (
                  <td className="px-4 py-3 text-sm">{t.funding_type || '-'}</td>
                )}
                {isColumnVisible('type_of_securities') && (
                  <td className="px-4 py-3 text-sm">{t.type_of_securities || '-'}</td>
                )}
                {isColumnVisible('number_of_securities') && (
                  <td className="px-4 py-3 text-sm text-right">{t.number_of_securities || '-'}</td>
                )}
                {isColumnVisible('consideration_per_security') && (
                  <td className="px-4 py-3 text-sm text-right">{t.consideration_per_security?.toLocaleString() || '-'}</td>
                )}
                {isColumnVisible('total_consideration') && (
                  <td className="px-4 py-3 text-sm text-right">{t.total_consideration?.toLocaleString()}</td>
                )}
                {isColumnVisible('percentage_shareholding') && (
                  <td className="px-4 py-3 text-sm text-right">{t.percentage_shareholding || '-'}</td>
                )}
                {/* Custom Field Columns */}
                {customFieldDefinitions.map((field) => 
                  isColumnVisible(`custom_${field.id}`) && (
                    <td key={field.id} className="px-4 py-3 text-sm">
                      {t.custom_fields?.[field.name] || '-'}
                    </td>
                  )
                )}
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => handleRowClick(t)}
                    className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    title="View Details"
                  >
                    Details
                  </button>
                </td>
                {(canEdit || canDelete) && (
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end space-x-2">
                      {canEdit && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(t);
                          }}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit transaction"
                        >
                          <Edit size={16} />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(t.id);
                          }}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                          title="Delete transaction (Admin only)"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {transactions.length === 0 && <p className="text-center py-8 text-gray-500">No transactions yet</p>}
      </div>

      <ColumnVisibilityModal
        isOpen={showColumnVisibility}
        onClose={() => setShowColumnVisibility(false)}
        columns={getAllColumns()}
        visibleColumns={columnVisibility}
        onToggleColumn={handleToggleColumn}
        onSelectAll={handleSelectAllColumns}
        onDeselectAll={handleDeselectAllColumns}
      />

      <DetailViewModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title={`Transaction Details - ${selectedTransaction?.type_of_transaction || ''}`}
        data={selectedTransaction}
        fields={[
          { key: 'date_of_transaction', label: 'Date', format: 'text' },
          { key: 'type_of_transaction', label: 'Transaction Type', format: 'text' },
          { key: 'funding_type', label: 'Funding Type', format: 'text' },
          { key: 'type_of_securities', label: 'Security Type', format: 'text' },
          { key: 'number_of_securities', label: 'Number of Securities', format: 'number' },
          { key: 'consideration_per_security', label: `Price per Security`, format: 'currency' },
          { key: 'total_consideration', label: `Total Consideration`, format: 'currency' },
          { key: 'percentage_shareholding', label: '% Holding', format: 'percentage' }
        ]}
        customFields={customFieldDefinitions}
        onEdit={canEdit ? handleEdit : null}
        onDelete={canDelete ? handleDelete : null}
        canModify={canEdit}
        currency={currency}
      />

      <BulkUploadModal
        isOpen={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        templateColumns={[]}
        templateData={getBulkUploadTemplate()}
        onUpload={handleBulkUpload}
        entityName="Transactions"
        filename={`${investment?.portfolio_company_name || 'Investment'}_Transactions`}
      />

      <ExcelExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        data={transactions}
        columns={exportColumns}
        filename={`${investment?.portfolio_company_name || 'Investment'}_Transactions`}
      />
    </div>
  );
};

export default TransactionsTab;
